import type { ITaiKhoanKeToanResponse } from '@/core/interfaces/ApiResponses';

export type HopDongFormMode = 'create' | 'edit';

export type HopDongWizardStep = 'meta' | 'hdvt';

export interface HopDongFormFields {
  so_hop_dong: string;
  ngay_ky: string;
  ngay_het_han: string;
  ten_doi_tac: string;
  so_dien_thoai?: string;
  dia_chi?: string;
  ma_so_thue: string;
  loai_hop_dong: number;
  so_hoa_don?: string;
  tk_ketoan: ITaiKhoanKeToanResponse;
}

/** Mandatory existing vật tư line */
export interface HopDongHdvtExistingInput {
  id_vat_tu: number;
  so_luong: number;
  so_tien: number;
  dac_tinh?: string;
}

/** Optional new vật tư line (inline create) */
export interface HopDongHdvtNewInput {
  ten_vat_tu: string;
  id_danh_muc: number[];
  don_vi_tinh: string;
  so_luong: number;
  so_tien: number;
  dac_tinh?: string;
}

export type HopDongHdvtInput = HopDongHdvtExistingInput | HopDongHdvtNewInput;

/** Raw / draft line before normalization */
export interface HopDongHdvtLineDraft {
  /** Client-only key for table rows */
  _key: string;
  kind: 'existing' | 'new';
  id_vat_tu?: number;
  ma_vat_tu?: string;
  ten_vat_tu?: string;
  id_danh_muc?: number[];
  ten_danh_muc?: string;
  don_vi_tinh?: string;
  so_luong: number;
  so_tien: number;
  dac_tinh?: string;
}

export function isHopDongHdvtExistingInput(
  line: HopDongHdvtInput
): line is HopDongHdvtExistingInput {
  return 'id_vat_tu' in line && typeof line.id_vat_tu === 'number';
}

export function normalizeHdvtLine(
  line: HopDongHdvtLineDraft | HopDongHdvtInput
): HopDongHdvtInput | null {
  const so_luong = Number(
    'so_luong' in line ? line.so_luong : 0
  );
  const so_tien = Number(
    'so_tien' in line ? line.so_tien : 0
  );

  if (!so_luong || so_luong <= 0 || so_tien < 0 || Number.isNaN(so_tien)) {
    return null;
  }

  const kind =
    'kind' in line
      ? line.kind
      : 'id_vat_tu' in line && line.id_vat_tu
        ? 'existing'
        : 'new';

  if (kind === 'existing') {
    const id_vat_tu =
      'id_vat_tu' in line && line.id_vat_tu ? Number(line.id_vat_tu) : 0;

    if (!id_vat_tu) {
      return null;
    }

    const item: HopDongHdvtExistingInput = {
      id_vat_tu,
      so_luong,
      so_tien,
    };

    const dac_tinh =
      'dac_tinh' in line && line.dac_tinh ? String(line.dac_tinh).trim() : '';

    if (dac_tinh) {
      item.dac_tinh = dac_tinh;
    }

    return item;
  }

  const ten_vat_tu =
    'ten_vat_tu' in line && line.ten_vat_tu ? String(line.ten_vat_tu).trim() : '';
  const don_vi_tinh =
    'don_vi_tinh' in line && line.don_vi_tinh
      ? String(line.don_vi_tinh).trim()
      : '';
  const id_danh_muc =
    'id_danh_muc' in line && Array.isArray(line.id_danh_muc)
      ? line.id_danh_muc.filter((id) => typeof id === 'number' && id > 0)
      : [];

  if (!ten_vat_tu || !don_vi_tinh || id_danh_muc.length === 0) {
    return null;
  }

  const item: HopDongHdvtNewInput = {
    ten_vat_tu,
    id_danh_muc,
    don_vi_tinh,
    so_luong,
    so_tien,
  };

  const dac_tinh =
    'dac_tinh' in line && line.dac_tinh ? String(line.dac_tinh).trim() : '';

  if (dac_tinh) {
    item.dac_tinh = dac_tinh;
  }

  return item;
}

/** POST /hop-dong/tao-moi */
export interface HopDongCreateRequest {
  hopDong: HopDongFormFields;
  hdvt: HopDongHdvtInput[];
}

/** POST /hop-dong/cap-nhat/{id} — full sync of hdvt when provided */
export interface HopDongUpdateRequest {
  hopDong: HopDongFormFields;
  hdvt: HopDongHdvtInput[];
}
